import { useCallback, useState } from 'react';

// 搜索框的「草稿 → 提交」两段式 (2026-09-18).
//
// 为什么不能边打边搜: 关键词搜的是前导通配 LIKE (`%kw%`), 用不了索引, 一次就是一遍全表扫。
// 美服慢日志实测 —— 参数页搜一个词, 敲到第 4 个字母时已经打出 4 轮查询, 每轮 78,783~123,376
// 行、0.5~1.6s, 而且列表、count、跨框架命中数各扫一遍。用户真正想搜的只是最后那一个词。
//
// 用法:
//   const kw = useSearchInput(initQ.get('keyword') || '', () => setPage(1));
//   <form onSubmit={(e) => { e.preventDefault(); kw.commit(); }}>
//     <input value={kw.draft} onChange={(e) => kw.setDraft(e.target.value)}
//            onKeyDown={kw.onKeyDown} onBlur={kw.commit} />
//   查询/URL 一律用 kw.value (已提交的那个), 不要用 draft。
export interface SearchInput {
    /** 输入框里的实时文本 (不触发查询) */
    draft: string;
    setDraft: (v: string) => void;
    /** 已提交的关键词: 查询与 URL 同步都用它 */
    value: string;
    /** 提交当前草稿 (回车 / 失焦 / 点搜索按钮) */
    commit: () => void;
    /** 回车即提交; 绑到 input 的 onKeyDown */
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * @param initial 初始关键词 (通常来自 URL)
 * @param onCommit 提交且确实变了时的回调 (通常是 setPage(1))
 */
export function useSearchInput(initial: string, onCommit?: () => void): SearchInput {
    const [draft, setDraftState] = useState(initial);
    const [value, setValue] = useState(initial);

    const apply = useCallback(
        (next: string) => {
            if (next === value) return;
            setValue(next);
            onCommit?.();
        },
        [value, onCommit],
    );

    // 清空是"取消搜索", 立即生效 —— 空关键词不产生 LIKE 扫描, 没必要再让用户按一次回车。
    const setDraft = useCallback(
        (v: string) => {
            setDraftState(v);
            if (v.trim() === '') apply('');
        },
        [apply],
    );

    const commit = useCallback(() => apply(draft.trim()), [apply, draft]);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                apply(draft.trim());
            }
        },
        [apply, draft],
    );

    return { draft, setDraft, value, commit, onKeyDown };
}
